/* global globalThis */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ScrollToTop from './ScrollToTop';
import PersistentOutlet from './PersistentOutlet';
import {
  Dashboard as DashboardIcon,
  ViewModule as ViewModuleIcon,
  Timeline as TimelineIcon,
  ShowChart as ShowChartIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Menu as MenuIcon,
  TrendingUp as TrendingUpIcon,
  Calculate as CalculateIcon,
  AutoAwesome as AIIcon,
  Diamond as DiamondIcon,
  Bolt as BoltIcon,
  Storage as StorageIcon,
  DataUsage as DataUsageIcon,
  Insights as InsightsIcon,
  Newspaper as NewspaperIcon,
  Speed as SpeedIcon,
  Waves as WavesIcon,
  Equalizer as EqualizerIcon,
  Explore as ExploreIcon,
  BarChart as BarChartIcon,
  Science as ScienceIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  SmartToy as SmartToyIcon,
  WorkOutline as WorkIcon,
  AccountBalance as AccountBalanceIcon,
  Stars as AutoAwesomeIcon,
  Shield as ShieldIcon,
  Healing as HealingIcon,
  SwapHoriz as SwapHorizIcon,
  CandlestickChart as CandlestickIcon,
  Replay as ReplayIcon,
  PivotTableChart as PivotIcon,
  Tune as TuneIcon,
  FlashOn as FlashOnIcon,
  SearchOff as SearchOffIcon,
  Login as EntryIcon,
  MonitorHeart as MonitorIcon,
  Gavel as GavelIcon,
  DonutSmall as DonutIcon,
  SentimentSatisfied as SentimentIcon,
  Thermostat as ThermostatIcon,
  Category as CategoryIcon,
  PieChart as PieChartIcon,
  CatchingPokemon as AutoPickIcon,
  Construction as BuilderIcon,
  EmojiEvents as TrophyIcon,
  AutoAwesome as AutoAwesomeIcon2,
  Psychology as PsychologyIcon,
  LocalFireDepartment as FireIcon2,
  Autorenew as AutorenewIcon,
  Visibility as VisibilityIcon,
  Security as SecurityIcon,
  HealthAndSafety as HealthShieldIcon,
  ScatterPlot as ScatterPlotIcon,
  QueryStats as QueryStatsIcon,
  Troubleshoot as TroubleshootIcon,
  Analytics as AnalyticsIcon,
  Leaderboard as LeaderboardIcon,
  SsidChart as SsidChartIcon,
  Inventory2 as InventoryIcon,
  StackedLineChart as StackedLineIcon,
  WaterDrop as WaterDropIcon,
  Radar as RadarIcon,
  Hub as HubIcon,
  Recommend as RecommendIcon,
  CallSplit as CallSplitIcon,
  NorthEast as NorthEastIcon,
  FitnessCenter as FitnessCenterIcon,
  ReceiptLong as ReceiptIcon,
  StarBorder as StarBorderIcon,
  VerticalAlignBottom as BottomIcon,
  AdminPanelSettings as AdminShieldIcon,
  CompareArrows as CompareIcon,
  RocketLaunch as RocketLaunchIcon,
  WbSunny as SunIcon,
  FilterAlt as FilterIcon,
  Grain as GrainIcon,
  AccountTree as TreeIcon,
  Compress as CompressIcon,
  CropFree as CropFreeIcon,
} from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import LanguageSwitcher from './LanguageSwitcher';
import BrandLogo, { BrandMark } from './brand/BrandLogo';
import './Layout.css';
import { useAuth } from '../AuthContext';

function Layout() {
  const { t } = useTranslation();
  const { isAuthenticated, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const browserWindow = typeof globalThis === 'object' ? globalThis.window : null;
  let toggleIcon = <ChevronRightIcon fontSize="small" />;

  if (isMobile) {
    toggleIcon = <MenuIcon fontSize="small" />;
  } else if (drawerOpen) {
    toggleIcon = <ChevronLeftIcon fontSize="small" />;
  }

  useEffect(() => {
    if (!browserWindow) return undefined;

    const checkMobile = () => {
      const mobile = browserWindow.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setDrawerOpen(false);
      }
    };

    checkMobile();
    browserWindow.addEventListener('resize', checkMobile);

    return () => browserWindow.removeEventListener('resize', checkMobile);
  }, [browserWindow]);

  useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
    }
  }, [location.pathname, isMobile]);

  const menuItems = [
    { text: t('navigation.dashboard'), icon: <DashboardIcon />, path: '/' },
    { text: t('navigation.morningBrief', 'Morning Brief'), icon: <SunIcon />, path: '/morning-brief' },
    { text: t('navigation.automationCenter', 'Automation Center'), icon: <SettingsIcon />, path: '/automation-center' },
    { text: t('navigation.marketPhaseDashboard', 'Pha thị trường'), icon: <RadarIcon />, path: '/market-phase-dashboard' },
    { text: t('navigation.stocks'), icon: <BarChartIcon />, path: '/stocks' },
    { text: t('navigation.alerts'), icon: <NotificationsIcon />, path: '/alerts' },
    {
      text: t('navigation.aiPredictions'),
      icon: <SmartToyIcon />,
      submenu: [
        { text: t('navigation.mlUpsideScreener'), icon: <TrendingUpIcon />, path: '/upside-dashboard' },
        { text: t('navigation.mlRiskIntel'), icon: <ShieldIcon />, path: '/risk-dashboard' },
        { text: t('navigation.riskComposite'), icon: <SecurityIcon />, path: '/risk-composite' },
        { text: t('navigation.mlTrend'), icon: <SsidChartIcon />, path: '/trend-predictions' },
        { text: t('navigation.recommendations'), icon: <RecommendIcon />, path: '/recommendations' },
        { text: t('navigation.tradingDecision'), icon: <GavelIcon />, path: '/trading-decision' },
      ],
    },
    {
      text: t('navigation.stockScreening'),
      icon: <ExploreIcon />,
      submenu: [
        { text: t('navigation.scanner'), icon: <TroubleshootIcon />, path: '/scanner' },
        { text: t('navigation.stockStrength', 'Stock Strength'), icon: <TrendingUpIcon />, path: '/stock-strength' },
        { text: t('navigation.ichimokuTransition'), icon: <ShowChartIcon />, path: '/ichimoku-transition' },
        { text: t('navigation.behaviorLabels', 'Behavior Labels'), icon: <PsychologyIcon />, path: '/behavior' },
        { text: t('navigation.autoPick'), icon: <AutoPickIcon />, path: '/auto-pick' },
        { text: t('navigation.score'), icon: <CalculateIcon />, path: '/score' },
        { text: t('navigation.saturationScore'), icon: <ThermostatIcon />, path: '/saturation' },
      ],
    },
    {
      text: t('navigation.trading'),
      icon: <CandlestickIcon />,
      submenu: [
        { text: t('navigation.smartEntry'), icon: <EntryIcon />, path: '/easy-step' },
        { text: t('navigation.tplusTrading'), icon: <SwapHorizIcon />, path: '/tplus' },
        { text: t('navigation.patternCluster', 'Pattern Cluster'), icon: <ScatterPlotIcon />, path: '/pattern-cluster' },
        { text: t('navigation.highVolRegime'), icon: <FlashOnIcon />, path: '/high-vol-regime' },
        { text: t('navigation.reversalCatcher'), icon: <ReplayIcon />, path: '/reversal-catcher' },
      ],
    },
    {
      text: t('navigation.smartMoney', 'Smart Money'),
      icon: <DiamondIcon />,
      submenu: [
        { text: t('navigation.commandCenter', 'Command Center'), icon: <RocketLaunchIcon />, path: '/smart-money-center' },
        { text: t('navigation.smartMoneyUnified', 'Smart Money Score'), icon: <AutoAwesomeIcon />, path: '/smart-money-unified' },
        { text: t('navigation.duplicateSignature', 'Đồng thuận Engine'), icon: <CompareIcon />, path: '/duplicate-signature' },
        { text: t('navigation.backtestValidation', 'Kiểm chứng'), icon: <AnalyticsIcon />, path: '/backtest-validation' },
        { text: t('navigation.pivotPoints', 'Pivot Points'), icon: <PivotIcon />, path: '/pivot-points' },
        { text: t('navigation.darvasBox', 'Darvas Box'), icon: <CropFreeIcon />, path: '/darvas-box' },
        { text: t('navigation.earlyPivot'), icon: <EntryIcon />, path: '/early-pivot' },
        { text: t('navigation.moneyFlow', 'Money Flow'), icon: <WavesIcon />, path: '/money-flow' },
        { text: t('navigation.distributionDetector', 'Distribution Detector'), icon: <WaterDropIcon />, path: '/distribution-detector' },
        { text: t('navigation.vpa', 'VPA Scanner'), icon: <CandlestickIcon />, path: '/vpa' },
        { text: t('navigation.vsa', 'VSA Scanner'), icon: <StackedLineIcon />, path: '/vsa' },
        { text: t('navigation.surgeReadiness', 'Surge Readiness'), icon: <RadarIcon />, path: '/surge-readiness' },
        { text: t('navigation.breakoutCandidates', 'Breakout Candidates'), icon: <RocketLaunchIcon />, path: '/breakout-candidates' },
        { text: t('navigation.marketInternalRadar', 'Market Internal Radar'), icon: <RadarIcon />, path: '/market-internal-radar' },
        { text: t('navigation.preparationPhase', 'Pha Chuẩn Bị'), icon: <FilterIcon />, path: '/preparation-phase' },
        { text: t('navigation.earlyAccumulation', 'Early Accumulation'), icon: <GrainIcon />, path: '/early-accumulation' },
        { text: t('navigation.bottomingFlow', 'Nội tại tích lũy'), icon: <CompressIcon />, path: '/bottoming-flow' },
        { text: t('navigation.silentAccumulation', 'Silent Accumulation'), icon: <InventoryIcon />, path: '/silent-accumulation' },
        { text: t('navigation.nicheRegime', 'Niche Regime'), icon: <TreeIcon />, path: '/niche-regime' },
        { text: t('navigation.marketDivergence', 'Market Divergence'), icon: <CallSplitIcon />, path: '/market-divergence' },
        { text: t('navigation.marketResilience'), icon: <NorthEastIcon />, path: '/market-resilience' },
      ],
    },
    {
      text: t('navigation.technicalAnalysis'),
      icon: <ShowChartIcon />,
      submenu: [
        { text: t('navigation.wyckoffPhases'), icon: <DonutIcon />, path: '/stock-phases' },
        { text: t('navigation.downtrendDetection'), icon: <TrendingDownIcon />, path: '/downtrend' },
        { text: t('navigation.downshiftScanner'), icon: <SearchOffIcon />, path: '/downshift-scanner' },
        { text: t('navigation.calibration'), icon: <TuneIcon />, path: '/calibration' },
        { text: t('navigation.modelAccuracy'), icon: <QueryStatsIcon />, path: '/model-accuracy' },
      ],
    },
    {
      text: t('navigation.portfolioManagement'),
      icon: <WorkIcon />,
      submenu: [
        { text: t('navigation.portfolioBuilder'), icon: <BuilderIcon />, path: '/portfolio-construction' },
        { text: t('navigation.safeInvest'), icon: <HealthShieldIcon />, path: '/safe-invest' },
        { text: t('navigation.statisticalRisk', 'Statistical Risk'), icon: <QueryStatsIcon />, path: '/statistical-risk' },
        { text: t('navigation.regimeWatchlist', 'Regime Watchlist'), icon: <AutorenewIcon />, path: '/regime-watchlist' },
        { text: t('navigation.riskOpportunity'), icon: <AnalyticsIcon />, path: '/risk-opportunity' },
        { text: t('navigation.portfolioRecommendations'), icon: <StarBorderIcon />, path: '/portfolio-recommendations' },
        { text: t('navigation.portfolioHealth'), icon: <MonitorIcon />, path: '/portfolio-health' },
        { text: t('navigation.riskManagement'), icon: <AdminShieldIcon />, path: '/risk-management' },
        { text: t('navigation.performanceDashboard'), icon: <LeaderboardIcon />, path: '/performance' },
        { text: t('navigation.paperTrading'), icon: <AccountBalanceIcon />, path: '/paper-trading' },
        { text: t('navigation.detectorPaperTrading', 'Detector Paper'), icon: <ReceiptIcon />, path: '/detector-paper-trading' },
        { text: t('navigation.detectorRecommendations', 'Khuyến Nghị CP'), icon: <FitnessCenterIcon />, path: '/detector-recommendations' },
      ],
    },
    {
      text: t('navigation.legendaryStrategies'),
      icon: <TrophyIcon />,
      submenu: [
        { text: t('navigation.legendaryOverview'), icon: <VisibilityIcon />, path: '/legendary-strategies' },
        { text: t('navigation.oneilCanslim'), icon: <AutoAwesomeIcon2 />, path: '/legendary-strategies/oneil' },
        { text: t('navigation.minerviniVcp'), icon: <PsychologyIcon />, path: '/legendary-strategies/minervini' },
        { text: t('navigation.weinsteinStage2'), icon: <StackedLineIcon />, path: '/legendary-strategies/weinstein' },
        { text: t('navigation.turtleDonchian'), icon: <WaterDropIcon />, path: '/legendary-strategies/turtle' },
        { text: t('navigation.livermorePivot'), icon: <FireIcon2 />, path: '/legendary-strategies/livermore' },
      ],
    },
    {
      text: t('navigation.macroMarket'),
      icon: <InsightsIcon />,
      submenu: [
        { text: t('navigation.macroForecast'), icon: <TimelineIcon />, path: '/forecast' },
        { text: t('navigation.macroOutlook'), icon: <RadarIcon />, path: '/outlook' },
        { text: t('navigation.sectorRotation'), icon: <CategoryIcon />, path: '/sector-rotation' },
        { text: t('navigation.icbIndustry', 'Phân ngành ICB'), icon: <HubIcon />, path: '/icb-industry' },
        { text: t('navigation.sectorMomentum'), icon: <SpeedIcon />, path: '/sector-momentum' },
        { text: t('navigation.marketRiskUnified', 'Market Risk'), icon: <WarningIcon />, path: '/market-risk' },
        { text: t('navigation.marketCorrection'), icon: <BottomIcon />, path: '/market-correction' },
        { text: t('navigation.marketRecovery'), icon: <HealingIcon />, path: '/market-recovery' },
        { text: t('navigation.hedgeDetection'), icon: <CompareIcon />, path: '/hedge-detection' },
        { text: t('navigation.sentiment'), icon: <SentimentIcon />, path: '/sentiment' },
        { text: t('navigation.marketBreadth'), icon: <EqualizerIcon />, path: '/market-breadth' },
        { text: t('navigation.concentration'), icon: <PieChartIcon />, path: '/concentration' },
      ],
    },
    {
      text: t('navigation.newsEvents'),
      icon: <NewspaperIcon />,
      submenu: [
        { text: t('navigation.aiNews'), icon: <AIIcon />, path: '/news-radar' },
        { text: t('navigation.shockIndex'), icon: <BoltIcon />, path: '/shock' },
      ],
    },
    {
      text: t('navigation.masterData'),
      icon: <StorageIcon />,
      submenu: [
        { text: t('navigation.sectorsManagement'), icon: <SettingsIcon />, path: '/sectors' },
        { text: t('navigation.observations'), icon: <DataUsageIcon />, path: '/observations' },
        { text: t('navigation.indicators'), icon: <ScienceIcon />, path: '/indicators' },
        { text: t('navigation.pillar'), icon: <ViewModuleIcon />, path: '/pillar' },
      ],
    },
  ];

  const toggleSubmenu = (text) => {
    setSubmenuOpen((prev) => ({
      ...prev,
      [text]: !prev[text],
    }));
  };

  const handleMenuItemClick = (item) => {
    if (item.submenu) {
      toggleSubmenu(item.text);
      return;
    }
    navigate(item.path);
  };

  return (
    <div className="layout">
      <aside className={`sidebar ${drawerOpen ? 'open' : 'closed'}`}>
        <button
          type="button"
          className="logo"
          onClick={() => navigate('/')}
          aria-label={t('app.title', 'IndicatorAI')}
        >
          {drawerOpen ? (
            <div className="logo-text">
              <BrandLogo className="brand-logo--sidebar" />
              <div className="logo-subtitle">{t('app.subtitle')}</div>
            </div>
          ) : (
            <BrandMark />
          )}
        </button>

        <nav className="menu">
          {menuItems.map((item) => (
            <div key={item.text}>
              <button
                type="button"
                className={`menu-item ${!item.submenu && location.pathname === item.path ? 'active' : ''}`}
                onClick={() => handleMenuItemClick(item)}
                aria-expanded={item.submenu ? Boolean(submenuOpen[item.text]) : undefined}
                aria-current={!item.submenu && location.pathname === item.path ? 'page' : undefined}
              >
                <div className="menu-icon">{item.icon}</div>
                {drawerOpen && (
                  <>
                    <span className="menu-text">{item.text}</span>
                    {item.submenu && (
                      <div className="submenu-indicator">
                        <ChevronRightIcon
                          fontSize="small"
                          sx={{
                            transform: submenuOpen[item.text] ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </button>

              {item.submenu && drawerOpen && submenuOpen[item.text] && (
                <div className="submenu">
                  {item.submenu.map((subitem) => (
                    <button
                      type="button"
                      key={subitem.text}
                      className={`menu-item submenu-item ${location.pathname === subitem.path ? 'active' : ''}`}
                      onClick={() => navigate(subitem.path)}
                      aria-current={location.pathname === subitem.path ? 'page' : undefined}
                    >
                      <div className="menu-icon">{subitem.icon}</div>
                      <span className="menu-text">{subitem.text}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {drawerOpen && (
          <div className="sidebar-footer">
            <div>{t('app.copyright')}</div>
            <div>{t('app.version')}</div>
          </div>
        )}

        <button
          className="toggle-btn"
          onClick={() => setDrawerOpen(!drawerOpen)}
          aria-label={drawerOpen ? t('navigation.closeSidebar') : t('navigation.openSidebar')}
        >
          {toggleIcon}
        </button>
      </aside>

      <div className="main-content">
        <header className="header">
          <div className="header-actions" style={{ marginLeft: 'auto' }}>
            <LanguageSwitcher />
            {isAuthenticated && (
              <Tooltip title={t('logout', 'Logout')} arrow>
                <IconButton
                  onClick={logout}
                  aria-label={t('logout', 'Logout')}
                  className="header-action-btn"
                  size="large"
                >
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </header>

        <main className="content">
          <ScrollToTop />
          <PersistentOutlet />
        </main>
      </div>
    </div>
  );
}

export default Layout;


